import { AdHocReportBuilder } from "../../ad-hoc-report-builder";
import { Step } from "../step";
import { defaultDataArgs } from "../../data/default-steps";

export class FormUI {
    private parent: AdHocReportBuilder;
    private wrapper: HTMLElement;
    private stepsWrapper: HTMLElement;
    private reportActionsWrapper: HTMLElement;
    private reportNameInput: HTMLInputElement;
    private reportSubmitButton: HTMLInputElement;

    constructor(parent: AdHocReportBuilder, wrapper: HTMLElement) {
        this.parent = parent;
        this.wrapper = wrapper;
    }

    /**
     * Build UI
     * 
     * @param {Step[]} steps
     */
    buildUi(steps: Step[]): void {
        // Add wrappers to wrapper
        this.stepsWrapper = document.createElement("div");
        this.stepsWrapper.id = "ahrb-steps-wrapper";
        this.wrapper.append(this.stepsWrapper);
        this.reportActionsWrapper = document.createElement("div");
        this.wrapper.append(this.reportActionsWrapper);

        this.addSteps(steps);

        this.addReportActions();
    }

    /**
     * Add steps
     * 
     * @param {Step[]} steps 
     */
    addSteps(steps: Step[]): void {
        // Add steps
        steps.forEach((item) => {
            const stepItem = document.createElement("div");
            stepItem.id = `ahrb-step-${item.name}`;
            stepItem.classList.add("step-item", "my-5");
            stepItem.innerHTML = `<h4 class="step-title font-weight-bold">${item.title}</h4>`
            stepItem.innerHTML += `<p class="step-description">${item.desc}</p>`;

            const formattedTitle = item.title.replace(/[\W_]/g, "_").toLowerCase();
            const stepInputWrapper = document.createElement("div");
            stepInputWrapper.classList.add("ahrb-step-input-wrapper");
            switch (item.type) {
                case "radio":
                    item.options.forEach((optionItem) => {
                        stepInputWrapper.innerHTML += `
                            <input type="radio" id="${optionItem.value}" name="${formattedTitle}" value="${optionItem.value}">
                            <label for="${optionItem.value}">${optionItem.title}</label>
                        `;

                        // Handle input change
                        stepInputWrapper.querySelectorAll(`input[name="${formattedTitle}"]`).forEach((inputItem) => {
                            inputItem.addEventListener("change", this.handleRadioChange.bind(this, item, steps));
                        });
                    })

                    break;
                case "multiSelect":
                    let multiSelectString = '';
                    multiSelectString += `<select id="${formattedTitle}" name="${formattedTitle}">`;
                    multiSelectString += `<option value="">Multi - Select</option>`;
                    multiSelectString += `<option value="select-all-items">ALL</option>`;

                    item.options.forEach((optionItem) => {
                        const classToApply = (optionItem.displayIf ? 'class="d-none"' : "");
                        multiSelectString += `<option value="${optionItem.value}" ${classToApply}>${optionItem.title}</option>`;
                    });

                    multiSelectString += `</select>`;

                    stepInputWrapper.innerHTML = multiSelectString;

                    const multiSelectChoosenValues = document.createElement("div");
                    multiSelectChoosenValues.classList.add("multi-select-choosen-values", "mt-3", "d-flex", "flex-wrap", "align-items-center");

                    stepInputWrapper.append(multiSelectChoosenValues);

                    // Handle input change
                    stepInputWrapper.querySelector(`select#${formattedTitle}`).addEventListener("change", this.handleMultiSelectChange.bind(this, multiSelectChoosenValues, item, steps));
                    break;
                case "select":
                    let selectString = '';
                    selectString += `<select id="${formattedTitle}" name="${formattedTitle}">`;
                    selectString += `<option value="">Select</option>`;

                    item.options.forEach((optionItem) => {
                        selectString += `<option value="${optionItem.value}">${optionItem.title}</option>`;
                    });

                    selectString += `</select>`;

                    stepInputWrapper.innerHTML = selectString;

                    // Handle input change
                    stepInputWrapper.querySelector(`select#${formattedTitle}`).addEventListener("change", this.handleSelectChange.bind(this, item, steps));

                    break;
            }

            stepItem.append(stepInputWrapper);

            this.stepsWrapper.append(stepItem);
        });
    }

    addReportActions(): void {
        this.reportActionsWrapper.id = "ahrb-report-actions-wrapper"
        this.reportActionsWrapper.classList.add("mt-5", "d-flex", "flex-wrap", "align-items-center");

        // Add actions
        this.reportActionsWrapper.innerHTML = "<h3 class='mr-5' style='flex-shrink: 0'>Report Name</h3>";

        // Add report name input
        this.reportNameInput = document.createElement("input");
        this.reportNameInput.style.flexGrow = "1";
        this.reportActionsWrapper.append(this.reportNameInput);

        // Add line break
        const flexItemBreak = document.createElement("div");
        flexItemBreak.style.flexBasis = "100%";
        flexItemBreak.style.height = "0";
        this.reportActionsWrapper.append(flexItemBreak);

        // Add report submit button
        this.reportSubmitButton = document.createElement("input");
        this.reportSubmitButton.value = "Submit";
        this.reportSubmitButton.type = "submit";
        this.reportSubmitButton.classList.add("gform_button", "button", "ml-auto", "mt-5");
        this.reportActionsWrapper.append(this.reportSubmitButton);

        // Add event listener for report submit button
        this.reportSubmitButton.addEventListener("click", this.handleReportSubmit.bind(this));
    }

    /**
     * Handle change on radio inputs
     * 
     * @param {Step} step
     * @param {Step[]} steps
     * @param {Event} event 
     */
    handleRadioChange(step: Step, steps: Step[], event: Event): void {
        const target = event.target as HTMLInputElement;

        const value = target.value;

        step.values.length = 0;
        step.values.push(value);

        this.handleDisplayIf(steps);
    }

    /**
     * Handle change on select inputs
     * 
     * @param {Step} step
     * @param {Step[]} steps
     * @param {Event} event 
     */
    handleSelectChange(step: Step, steps: Step[], event: Event): void {
        const target = event.target as HTMLSelectElement;
        const value = target.value;

        // Remove custom field wrapper
        target.closest(".ahrb-step-input-wrapper").querySelector(".custom-field-wrapper")?.remove();

        // Check for selected custom fields
        const selectedCustomField = step?.customFields?.find((item) => item.option == target.value);
        if (selectedCustomField) {

            switch (selectedCustomField.type) {
                case "datePicker":
                    const customFieldWrapper = document.createElement("div");
                    customFieldWrapper.classList.add("custom-field-wrapper", "mt-4");

                    // Add from date picker
                    const fromDateInput = document.createElement("input");
                    fromDateInput.type = "date";
                    fromDateInput.classList.add("from-date");
                    fromDateInput.name = "from-date"
                    customFieldWrapper.append(fromDateInput);

                    // Add change event listener
                    fromDateInput.addEventListener("change", this.handleDatePickerChange.bind(this, step, 0));

                    // Add divider
                    const divider = document.createElement("span");
                    divider.innerText = "-";
                    divider.classList.add("mx-4");
                    customFieldWrapper.append(divider);

                    // Add to date picker
                    const toDateInput = document.createElement("input");
                    toDateInput.type = "date";
                    toDateInput.classList.add("to-date");
                    toDateInput.name = "to-date";
                    customFieldWrapper.append(toDateInput);

                    // Add change event listener
                    toDateInput.addEventListener("change", this.handleDatePickerChange.bind(this, step, 1));

                    // Add date picker wrapper to step input wrapper
                    target.closest(".ahrb-step-input-wrapper").append(customFieldWrapper);

                    break;
            }
        } else {
            // Add value to step
            step.values.length = 0;
            if (value) step.values.push(value);
        }

        this.handleDisplayIf(steps);
    }

    /**
     * Handle select change on multi select elements
     * 
     * @param {HTMLDivElement} multiSelectChoosenValues 
     * @param {Step} step
     * @param {Step[]} steps
     * @param {Event} event 
     */
    handleMultiSelectChange(multiSelectChoosenValues: HTMLDivElement, step: Step, steps: Step[], event: Event): void {
        const target = event.target as HTMLSelectElement;

        const value = target.value;
        const title = target.selectedOptions[0].innerText;

        // Don't do anything if has no value, or the same as already added
        if (!value || step.values.find((item) => {
            if (typeof item === 'object' && item.name == value) {
                return item;
            }
        })) {
            return;
        }

        // Choose all items if value is "select-all-items"
        if (value === "select-all-items") {
            const optionsArray = Array.from(target.options);

            for (let i = 0; i < optionsArray.length; i++) {
                const optionItem = optionsArray[i];

                // Ignore empty, ALL or hidden values
                if (!optionItem.value || optionItem.value === "select-all-items" || optionItem.classList.contains("d-none")) continue;

                // Select item
                target.selectedIndex = i;
                target.dispatchEvent(new Event("change"));

                // Set back to ALL
                target.value = "select-all-items";
            }
        } else {
            this.addMultiSelectChosenItem(multiSelectChoosenValues, step, steps, event, value, title);
        }

        this.toggleMultiSelectResetButton(target.closest(".ahrb-step-input-wrapper"), step);
    }

    /**
     * Add item to multi select chosen items
     * 
     * @param {HTMLDivElement} multiSelectChoosenValues 
     * @param {Step} step
     * @param {Step[]} steps
     * @param {Event} event
     * @param {string} value
     * @param {string} title
    */
    addMultiSelectChosenItem(multiSelectChoosenValues: HTMLDivElement, step: Step, steps: Step[], event: Event, value: string, title: string) {
        const multiSelectChoosenItem = document.createElement("span");
        multiSelectChoosenItem.classList.add("multi-select-choosen-item");
        multiSelectChoosenItem.setAttribute("data-value", value);
        multiSelectChoosenItem.innerHTML = `<span class="multi-select-choosen-item-delete"><i class="fa fa-times"></i></span>
        <span class="multi-select-choosen-item-title">${title}</span>`;
        multiSelectChoosenValues.append(multiSelectChoosenItem);

        multiSelectChoosenItem.querySelector(".multi-select-choosen-item-delete").addEventListener("click", ((event) => {
            this.handleMultiSelectChoosenItemDelete(step, event);
        }));

        step.values.push({ title: title, name: value });

        const selectedCustomField = step?.customFields?.find((item) => item.option == value);

        if (selectedCustomField) {
            switch (selectedCustomField.type) {
                case "bodyPartValuePicker":
                    const addCompareButton = document.createElement("span");
                    addCompareButton.classList.add("multi-select-choosen-item-add-compare");
                    addCompareButton.innerHTML = '<i class="fa fa-plus"></i>';
                    multiSelectChoosenItem.append(addCompareButton);
                    addCompareButton.addEventListener("click", this.handleMultiSelectAddCompare.bind(this, multiSelectChoosenItem, step));
                    break;
            }
        }

        this.handleDisplayIf(steps);
    }

    /**
     * Handle multi select choosen item deletion
     * 
     * @param {Step} step 
     * @param {Event} event 
     */
    handleMultiSelectChoosenItemDelete(step: Step, event: Event): void {
        const target = event.target as HTMLSpanElement;
        const parent = target.closest(".multi-select-choosen-item") as HTMLSpanElement;
        const value = parent.dataset.value;
        const index = step.values.findIndex((item) => {
            if (typeof item === 'object' && item !== null && 'name' in item) {
                return item.name === value;
            }
            return false;
        });
        step.values.splice(index, 1);
        
        this.toggleMultiSelectResetButton(target.closest(".ahrb-step-input-wrapper"), step);
        
        parent.remove();
    }

    /**
     * Handle add compare button click on multi select choosen item
     * 
     * @param {HTMLSpanElement} multiSelectChoosenItem 
     * @param {Step} step 
     * @param {Event} event 
     */
    handleMultiSelectAddCompare(multiSelectChoosenItem: HTMLSpanElement, step: Step, event: Event): void {
        const target = event.target as HTMLSpanElement;

        // Add elements
        if (target.classList.contains("fa-plus")) {
            target.classList.remove("fa-plus");
            target.classList.add("fa-minus");

            const compareElement = document.createElement("select");
            compareElement.classList.add("multi-select-choosen-item-compare");
            compareElement.innerHTML = `
                        <option value="">Please select</option>
                        <option value="equal">Equal</option>
                        <option value="greaterThan">Greater than</option>
                        <option value="smallerThan">Smaller than</option>`;
            multiSelectChoosenItem.append(compareElement);

            const valueElement = document.createElement("select");
            valueElement.classList.add("multi-select-choosen-item-value");
            valueElement.innerHTML = `
                        <option value="">Please select</option>
                        <option value="0">0</option>
                        <option value="1">1</option>
                        <option value="2">2</option>
                        <option value="3">3</option>
                        <option value="4">4</option>
                        <option value="5">5</option>
                        <option value="6">6</option>
                        <option value="7">7</option>
                        <option value="8">8</option>
                        <option value="9">9</option>
                        <option value="10">10</option>`;
            multiSelectChoosenItem.append(valueElement);

            // Add change event listeners
            multiSelectChoosenItem.querySelectorAll(".multi-select-choosen-item-compare, .multi-select-choosen-item-value").forEach((item) => {
                item.addEventListener("change", this.handleMultiSelectCompareChange.bind(this, step));
            });
        } else {
            // Delete elements
            target.classList.remove("fa-minus");
            target.classList.add("fa-plus");

            multiSelectChoosenItem.querySelectorAll(".multi-select-choosen-item-compare, .multi-select-choosen-item-value").forEach((item) => {
                item.remove();
            });

            // Delete compare and value from step values
            const value = multiSelectChoosenItem.dataset.value;
            const index = step.values.findIndex((item) => {
                if (typeof item === 'object' && item !== null && 'name' in item) {
                    return item.name === value;
                }
                return false;
            });
            if (index !== -1) {
                if (typeof step.values[index] === 'object' && step.values[index].hasOwnProperty('compare')) {
                    delete (step.values[index] as { compare: string }).compare;
                }

                if (typeof step.values[index] === 'object' && step.values[index].hasOwnProperty('value')) {
                    delete (step.values[index] as { value: string }).value;
                }
            }
        }
    }

    /**
     * Handle change on compare select elements
     * 
     * @param {Step} step 
     * @param {Event} event 
     */
    handleMultiSelectCompareChange(step: Step, event: Event): void {
        const target = event.target as HTMLSelectElement;
        const parent = target.closest(".multi-select-choosen-item") as HTMLSpanElement;
        const value = parent.dataset.value;
        const index = step.values.findIndex((item) => {
            if (typeof item === 'object' && item !== null && 'name' in item) {
                return item.name === value;
            }
            return false;
        });
        if (index !== -1) {
            switch (target.classList[0]) {
                case "multi-select-choosen-item-compare":
                    if (typeof step.values[index] === 'object') {
                        (step.values[index] as { compare: string }).compare = target.value;
                    }
                    break;
                case "multi-select-choosen-item-value":
                    if (typeof step.values[index] === 'object') {
                        (step.values[index] as { value: string }).value = target.value;
                    }
                    break;
            }
        }
    }

    /**
     * Toggle multi select reset button
     * 
     * @param {HTMLSelectElement} target Parent of the multi select with the class of "ahrb-step-input-wrapper"
     */
    toggleMultiSelectResetButton(target: HTMLDivElement, step: Step) {
        const existingResetButton = target.querySelector('.multiselect-reset-button');
        if (existingResetButton) {
            if (!step.values.length) {
                existingResetButton.remove();
            }
        } else {
            if (step.values.length) {
                const resetButton = document.createElement('button');
                resetButton.classList.add('multiselect-reset-button', 'ergmaps-button', 'small-button', 'ml-3');
                resetButton.innerText = "RESET";
        
                // Add reset button next to multi select
                target.insertBefore(resetButton, target.querySelector('select').nextSibling);

                resetButton.addEventListener('click', this.handleMultiSelectReset.bind(this, target));
            }
        }
    }

    /**
     * Handle reseting a multi select
     * 
     * @param {HTMLDivElement} target
     */
    handleMultiSelectReset(target: HTMLDivElement) {
        // Reset select
        target.querySelector('select').value = '';

        // Remove elements
        target.querySelectorAll('.multi-select-choosen-item-delete .fa-times').forEach((item: HTMLElement) => {
            item.click();
        });
    }

    /**
     * Handle date picker change
     * 
     * @param {Step} step
     * @param {number} indexInArray The index of the value in the step values array
     * @param {Event} event
     */
    handleDatePickerChange(step: Step, indexInArray: number, event: Event): void {
        const target = event.target as HTMLInputElement;
        const value = target.value;

        step.values[indexInArray] = value;
    }

    /**
     * Handle displaying the options controlled by displayIf property of the options
     * 
     * @param {Step[]} steps
     */
    handleDisplayIf(steps: Step[]): void {
        steps.map((stepItem) => {
            stepItem.options.filter((optionItem) => optionItem.displayIf) // Filter out options without displayIf property
                .map((optionItem) => {
                    // If any of the displayIf array groups is true, display the option
                    let shouldDisplay = false;

                    for (const displayIfArrayGroup of optionItem.displayIf) {
                        // If any of the displayIf arrays is true, display the option
                        let displayIfMatches = true;

                        displayIfItemLoop: for (const displayIfItem of displayIfArrayGroup) {
                            // Find the steps marked in displayIfItem and check if their values match
                            for (const step of steps) {
                                if (step.name === displayIfItem.stepName) {
                                    if (step.values.length) {
                                        for (const value of step.values) {
                                            if (typeof value === 'object' && value.value !== displayIfItem.value) {
                                                displayIfMatches = false;
                                                break displayIfItemLoop;
                                            } else if (value !== displayIfItem.value) {
                                                displayIfMatches = false;
                                                break displayIfItemLoop;
                                            }
                                        }
                                    } else {
                                        displayIfMatches = false;
                                        break displayIfItemLoop;
                                    }
                                }
                            }
                        }

                        if (displayIfMatches) {
                            shouldDisplay = true;
                            break;
                        }
                    };

                    if (shouldDisplay) {
                        document.querySelector(`#ahrb-step-${stepItem.name} option[value="${optionItem.value}"]`)?.classList.remove("d-none");
                    } else {
                        // Hide option
                        document.querySelector(`#ahrb-step-${stepItem.name} option[value="${optionItem.value}"]`)?.classList.add("d-none");

                        // Remove value from ui and step on multi select
                        const multiSelectDelete = document.querySelector(`#ahrb-step-${stepItem.name} .multi-select-choosen-item[data-value="${optionItem.value}"] .multi-select-choosen-item-delete`) as HTMLElement;
                        if (multiSelectDelete) multiSelectDelete.click();

                        // Reset step on select
                        const select = document.querySelector(`#ahrb-step-${stepItem.name} select`) as HTMLSelectElement;
                        // Reset select value if selected option is hidden
                        if (select && select.options[select.selectedIndex].classList.contains('d-none')) select.value = "";
                    }

                });
        });
    }

    /**
     * Handle report submit
     * 
     * @param {Event} event
     */
    handleReportSubmit(event: Event): void {
        event.preventDefault();

        // Call parent's report submit handler
        this.parent.onReportSubmit();
    }

    /**
     * Get the name of the report
     */
    getReportName(): string {
        return this.reportNameInput.value;
    }

    /**
     * Set values in the form
     * 
     * @param {string} title
     * @param {Object} steps
     */
    setValuesOfSteps(title: string, steps: {
        typeOfReport: (string | number | { title: string, name: string, compare?: string, value?: string })[]
        worksites: (string | number | { title: string, name: string, compare?: string, value?: string })[]
        dataType: (string | number | { title: string, name: string, compare?: string, value?: string })[]
        timeframe: (string | number | { title: string, name: string, compare?: string, value?: string })[]
        columns: (string | number | { title: string, name: string, compare?: string, value?: string })[]
    }) {
        // Set report title
        this.reportNameInput.value = title;

        // Set steps
        defaultDataArgs.forEach((step) => {
            const stepWrapper = document.getElementById(`ahrb-step-${step.name}`);
            const stepType = step.type;
            const stepFormattedTitle = step.title.replace(/[\W_]/g, "_").toLowerCase();

            if (stepType === "radio") {
                const radioInput = stepWrapper.querySelector(`#${steps[step.name]}`) as HTMLInputElement;
                radioInput.click();
            }

            if (stepType === "multiSelect") {
                const multiSelectInput = stepWrapper.querySelector(`#${stepFormattedTitle}`) as HTMLInputElement;
                
                // Select all the selected options
                steps[step.name].forEach((option) => {
                    multiSelectInput.value = option.name;
                    multiSelectInput.dispatchEvent(new Event('change'));

                    // Set comparsion
                    if (option.compare) {
                        const choosenItem = stepWrapper.querySelector(`.multi-select-choosen-values .multi-select-choosen-item[data-value="${option.name}"]`);
                        const choosenItemAddCompare = choosenItem.querySelector('.multi-select-choosen-item-add-compare i') as HTMLSpanElement;
                        choosenItemAddCompare.click();

                        const chosenItemCompareSelect = choosenItem.querySelector('.multi-select-choosen-item-compare') as HTMLSelectElement;
                        chosenItemCompareSelect.value = option.compare;
                        chosenItemCompareSelect.dispatchEvent(new Event('change'));

                        const chosenItemValueSelect = choosenItem.querySelector('.multi-select-choosen-item-value') as HTMLSelectElement;
                        chosenItemValueSelect.value = option.value;
                        chosenItemValueSelect.dispatchEvent(new Event('change'));
                    }
                });
            }

            if (stepType === "select") {
                const selectInput = stepWrapper.querySelector(`#${stepFormattedTitle}`) as HTMLSelectElement;

                if (steps[step.name].length === 2) {
                    selectInput.value = 'custom';
                    selectInput.dispatchEvent(new Event('change'));

                    const fromDateInput = stepWrapper.querySelector('input[name="from-date"]') as HTMLInputElement;
                    fromDateInput.value = steps[step.name][0];
                    fromDateInput.dispatchEvent(new Event('change'));

                    const toDateInput = stepWrapper.querySelector('input[name="to-date"]') as HTMLInputElement;
                    toDateInput.value = steps[step.name][1];
                    toDateInput.dispatchEvent(new Event('change'));

                } else {
                    selectInput.value = `${steps[step.name]}`;
                    selectInput.dispatchEvent(new Event('change'));
                }
            }
        });

        // Submit form
        this.reportSubmitButton.click();
    }
}
